import React, { Component } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Form,
} from "reactstrap";
import {
  APP_CONST,
  setStoredItem,
  isLogin,
  getStoredItem,
  resetStorage,
} from "@/store/dataStore";
import swal from "sweetalert2";
import { Link } from "react-router-dom";

import LoadingButtonIcon from "@/views/Components/LoadingButtonIcon";
import DefaultModal from "@/views/Components/DefaultModal";
import authApi from "@/api/auth";
import keyholderApi from "@/api/keyholder";
import { handleError } from "@/util/errorHandler";
import properties from "@/api/properties";
import { ChangePassword } from "@/views";
import { ERROR_CODES } from "@/store/constants";

class Login extends Component {
  state = {
    username: "",
    password: "",
    description: "",
    userType: "",
    token: "",
    isLoading: false,
    isChangePasswordRequired: false,
    isChangePasswordModalOpened: false,
    changePasswordReason: "",
  };

  componentDidMount() {
    this.checkLogin();
    document.addEventListener("visibilitychange", function() {
      if (!document.hidden && isLogin()) {
        window.location.href = "/";
      }
    });
  }

  checkLogin = async () => {
    if (isLogin()) {
      this.redirectTo(getStoredItem(APP_CONST.USER_TYPE));
    }
  };

  authorize = async () => {
    const userData = await authApi.fetchUserData(this.state.username);

    const { auth, permissions } = JSON.parse(
      atob(this.state.token.split(".")[1]),
    );
    const authorities = auth.split(",");

    const {
      bin,
      participantCode,
      accountId,
      mobileRegistrationVerificationType,
    } = await properties.getBankInfo();
    setStoredItem(
      APP_CONST.REGISTRATION_TYPE,
      mobileRegistrationVerificationType,
    );
    setStoredItem(APP_CONST.SEC_TOKEN, this.state.token);
    setStoredItem(
      APP_CONST.USER_INFO,
      JSON.stringify({
        ...userData,
        participantBankName: bin,
        participantBankCode: participantCode,
        accountId,
      }),
    );
    setStoredItem(APP_CONST.USERNAME, this.state.username);
    setStoredItem(APP_CONST.USER_TYPE, auth === "" ? undefined : auth);
    setStoredItem(
      APP_CONST.USER_AUTHORITIES,
      JSON.stringify(authorities || []),
    );
    setStoredItem(
      APP_CONST.USER_PERMISSIONS,
      JSON.stringify(permissions || []),
    );
  };

  handlePasswordChange = async newPassword => {
    this.setState({ password: newPassword });
    await this.handleLogin();
  };

  handleModalClose = async () => {
    this.setState({ isChangePasswordModalOpened: false });
    try {
      this.setState({ isLoading: true });
      await this.authorize();
      await this.redirectTo(getStoredItem(APP_CONST.USER_TYPE));
    } catch (err) {
      handleError(err, { title: err.detail });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleLogin = async event => {
    if (event) {
      event.preventDefault();
    }
    resetStorage();

    try {
      this.setState({ isLoading: true });
      const { username, password } = this.state;
      const id_token = await authApi.login({
        username,
        password,
        rememberMe: true,
      });
      this.setState({ token: id_token });

      await this.authorize();
      await this.redirectTo(getStoredItem(APP_CONST.USER_TYPE));
    } catch (err) {
      if (err.data) {
        this.setState({ token: err.data.id_token });
      }

      if (
        err.errorCode === ERROR_CODES.PASSWORD_EXPIRES ||
        err.errorCode === ERROR_CODES.PASSWORD_EXPIRED ||
        err.errorCode === ERROR_CODES.FORCE_CHANGE_PASSWORD
      ) {
        if (
          err.errorCode === ERROR_CODES.PASSWORD_EXPIRED ||
          err.errorCode === ERROR_CODES.FORCE_CHANGE_PASSWORD
        ) {
          this.setState({ isChangePasswordRequired: true });
        }

        this.setState({ changePasswordReason: err.message });
        this.setState({ isChangePasswordModalOpened: true });
        this.setState({ isLoading: false });

        return;
      }

      handleError(err, { title: err.detail });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleReinstallation = () => {
    setStoredItem(APP_CONST.CURRENT_ACTION, APP_CONST.ACTION_REINSTALL);
    setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_RE_INSTALL);
    this.props.history.push("/keyholder/re-install");
  };

  redirectTo = async role => {
    switch (role) {
      case APP_CONST.USER_TYPE_KEYHOLDER:
        return this.handleKeyholderAuth();

      case APP_CONST.USER_TYPE_ADMIN:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_ADMIN);
        return this.props.history.push("/waiting_for_approval");

      case APP_CONST.USER_TYPE_MANAGER:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_MANAGER);
        return this.props.history.push("/dashboard");

      case APP_CONST.USER_TYPE_ACCESS_MANAGER:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_ACCESS_MANAGER);
        return this.props.history.push("/users");

      case APP_CONST.USER_TYPE_AUDITOR:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_AUDITOR);
        return this.props.history.push("/auditor-users");

      case APP_CONST.USER_TYPE_AUTHORIZER:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_AUTHORIZER);
        return this.props.history.push("/recovery-requests");
      default:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_CUSTOM);
        return this.props.history.push("/blank-page");
    }
  };

  handleKeyholderAuth = async () => {
    const { status } = await keyholderApi.getStatusOfCurrentCase();

    switch (status) {
      default:
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_REGISTRATION);

        return this.props.history.push("/keyholder/registration");

      case "RE_INSTALLATION":
        setStoredItem(APP_CONST.CURRENT_ACTION, APP_CONST.ACTION_REINSTALL);
        setStoredItem(APP_CONST.APP_NAV_MENU, APP_CONST.NAV_RE_INSTALL);

        return this.props.history.push("/keyholder/re-install");

      case "NO_WRITE_PERMISSION":
        const error = new Error(
          "There is no write permission in the server file system. The key pair cannot be saved. Call an administrator to fix this issue",
        );

        return handleError(error, { title: "Key pair generation failed" });

      case "RESEND_SECRETS":
        const { value: isAccepted } = await swal({
          title: "Key pair generation failed",
          text: "Key pair hasn't generated. Try to generate it again?",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Generate",
          cancelButtonText: "Cancel",
          reverseButtons: true,
        });

        if (isAccepted) {
          await keyholderApi.createBankAccountRepeatRequest();
          await swal({
            title: "Key pair is generating",
            text: "Bank account is in the process of creation!",
            type: "success",
          });
        }
    }
  };

  handleChangeUsername = ({ target: { value: username } }) =>
    this.setState({ username });

  handleChangePassword = ({ target: { value: password } }) => {
    this.setState({ password });
  };

  getLoginForm = () => {
    return (
      <Col lg="6" md="8">
        <Card
          className="p-4"
          style={{
            borderColor: "#f2ecec",
            borderWidth: 10,
          }}>
          <CardBody>
            <h1>Login</h1>
            <Form onSubmit={this.handleLogin}>
              <InputGroup className="mb-3">
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <i className="icon-key" />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  type="text"
                  autoFocus
                  placeholder="Username"
                  value={this.state.username}
                  onChange={this.handleChangeUsername}
                  data-cy="username"
                />
              </InputGroup>
              <InputGroup className="mb-4">
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <i className="icon-lock" />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  type="password"
                  placeholder="Password"
                  value={this.state.password}
                  onChange={this.handleChangePassword}
                  data-cy="password"
                />
              </InputGroup>
              <Row className="justify-content-center mb-3">
                <Button
                  type="submit"
                  color="primary"
                  className={this.state.isLoading ? "" : "px-4"}
                  disabled={
                    this.state.isLoading ||
                    !this.state.username ||
                    !this.state.password
                  }
                  data-cy="submit">
                  <span>
                    Login
                    <LoadingButtonIcon loading={this.state.isLoading} />
                  </span>
                </Button>
              </Row>
              <Row className="justify-content-center">
                <Link
                  to={{
                    pathname: `/forgot-password`,
                  }}>
                  <Button type="button">
                    <span>Forgot password</span>
                  </Button>
                </Link>
              </Row>
            </Form>
          </CardBody>
        </Card>
        <p
          className="text-muted"
          style={{ marginTop: "16px", textAlign: "center" }}>
          {(() => {
            const systemVersion = window.env && window.env.SYSTEM_VERSION;
            const portalVersion = window.env && window.env.PORTAL_VERSION;
            const parts = [];
            if (systemVersion) parts.push(`System version: ${systemVersion}`);
            if (portalVersion) parts.push(`Portal version: ${portalVersion}`);
            return parts.length ? (
              <>
                {parts.join(" • ")}
                <br />
              </>
            ) : null;
          })()}
          Email:{" "}
          <a target="_top" href="mailto:bakong@nbc.org.kh">
            bakong@nbc.org.kh
          </a>
          <br />
          <a
            target="_blank"
            href="https://www.nbc.org.kh/download_files/bakong/Bakong_payment_gateway_user_manual.pdf"
            rel="noopener noreferrer">
            User manual
          </a>
        </p>
      </Col>
    );
  };

  render() {
    return (
      <div
        className="app flex-row align-items-center"
        style={{
          backgroundColor: "#333131",
        }}>
        <Container>
          <Row className="justify-content-center">{this.getLoginForm()}</Row>
        </Container>

        <DefaultModal isOpen={this.state.isChangePasswordModalOpened}>
          <ChangePassword
            onPasswordChange={this.handlePasswordChange}
            isCancelable={!this.state.isChangePasswordRequired}
            onCancel={this.handleModalClose}
            message={this.state.changePasswordReason}
          />
        </DefaultModal>
      </div>
    );
  }
}

export default Login;
